import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Chip,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import { fetchWithAuth } from '../../../utils/apiAuth';
import config from '../../../config';
import { downloadCsvFile } from '../../../utils/csvDownload';
import {
  formatAccessEndDateInputValue,
  getAccessEndFromCancelledAt,
  getManualSubscriptionCurrentStatus
} from '../../../utils/subscriptionExpiry';
import { useSuperDuper } from '../context/SuperDuperContext';

// Access end shown/edited in UI; API saves cancelledAt only (end date − 1 month).
const apiBaseUrl = config.getApiUrl();

const CSV_HEADERS = [
  'Name',
  'Email',
  'Phone',
  'Mode',
  'Payment ID',
  'Amount',
  'Access ends',
  'Status',
  'Days remaining'
];

const SECTION_CONFIG = {
  active: {
    title: 'Active',
    subtitle: 'On going — before the final month',
    headerBg: 'rgba(46, 125, 50, 0.12)',
    borderColor: '#2e7d32',
    paperBg: 'rgba(46, 125, 50, 0.04)'
  },
  endingSoon: {
    title: 'Ending Soon',
    subtitle: 'Final month — member expiry banner is active',
    headerBg: 'rgba(255, 107, 53, 0.18)',
    borderColor: '#FF6B35',
    paperBg: 'rgba(255, 107, 53, 0.06)'
  },
  ended: {
    title: 'Ended',
    subtitle: 'Access period is over',
    headerBg: 'rgba(97, 97, 97, 0.14)',
    borderColor: '#757575',
    paperBg: 'rgba(97, 97, 97, 0.04)'
  }
};

const emptyForm = () => ({
  customerName: '',
  email: '',
  phone: '',
  childAge: '',
  childGender: '',
  paymentId: '',
  paymentAmount: '',
  endingAt: '',
  subscriptionMode: ''
});

function partitionManualSubscriptions(subscriptions) {
  const active = [];
  const endingSoon = [];
  const ended = [];

  subscriptions.forEach((sub) => {
    const { type } = getManualSubscriptionCurrentStatus(sub);
    if (type === 'ended') {
      ended.push(sub);
    } else if (type === 'banner_active') {
      endingSoon.push(sub);
    } else {
      active.push(sub);
    }
  });

  return { active, endingSoon, ended };
}

function formatAccessEnd(sub) {
  if (!sub.cancelledAt) return '—';
  return getAccessEndFromCancelledAt(sub.cancelledAt).toLocaleDateString();
}

function statusLabelForCsv(sub) {
  const status = getManualSubscriptionCurrentStatus(sub);
  if (status.type === 'ended') return 'Ended';
  if (status.type === 'banner_active') return 'Ending Soon';
  if (status.type === 'ongoing') return 'On going';
  return '—';
}

function subscriptionToCsvRow(sub) {
  const status = getManualSubscriptionCurrentStatus(sub);
  const daysRemaining =
    status.type === 'banner_active' ? String(status.daysRemaining) : '';

  return [
    sub.customerName || '',
    sub.email || '',
    sub.phone || '',
    sub.subscriptionMode || '',
    sub.paymentDetails?.razorpay_payment_id || '',
    sub.monthlyAmount ?? '',
    formatAccessEnd(sub),
    statusLabelForCsv(sub),
    daysRemaining
  ];
}

const ManualSubscriptionsTab = () => {
  const { showSnackbar } = useSuperDuper();
  const [modes, setModes] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editAccessEndTarget, setEditAccessEndTarget] = useState(null);
  const [editEndingAt, setEditEndingAt] = useState('');
  const [savingAccessEnd, setSavingAccessEnd] = useState(false);

  const partitioned = useMemo(() => partitionManualSubscriptions(rows), [rows]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [metadata, subscriptions] = await Promise.all([
        fetchWithAuth(`${apiBaseUrl}/api/manual-subscriptions/metadata`),
        fetchWithAuth(`${apiBaseUrl}/api/manual-subscriptions`)
      ]);
      setModes(metadata.modes || []);
      setRows(subscriptions || []);
    } catch (err) {
      setError(err.message || 'Failed to load manual subscriptions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDownloadCsv = (sectionKey, sectionRows) => {
    const configSection = SECTION_CONFIG[sectionKey];
    const fileName = `YoungPresso_Manual_Subs_${configSection.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    const count = downloadCsvFile({
      headers: CSV_HEADERS,
      rows: sectionRows.map(subscriptionToCsvRow),
      fileName
    });
    showSnackbar(`CSV downloaded (${count} records)`, 'success');
  };

  const renderStatusCell = (sub) => {
    const status = getManualSubscriptionCurrentStatus(sub);

    if (status.type === 'ended') {
      return (
        <Typography variant="body2" color="text.secondary">
          Ended
        </Typography>
      );
    }
    if (status.type === 'banner_active') {
      const days = status.daysRemaining;
      const dayLabel = days === 1 ? 'day' : 'days';
      return (
        <Chip
          size="small"
          label={`Ending Soon (${days} ${dayLabel})`}
          sx={{
            fontWeight: 600,
            backgroundColor: 'rgba(255, 107, 53, 0.2)',
            color: '#E65100',
            border: '1px solid rgba(255, 107, 53, 0.4)'
          }}
        />
      );
    }
    if (status.type === 'unknown') {
      return '—';
    }
    return (
      <Typography variant="body2">
        On going
      </Typography>
    );
  };

  const renderSubscriptionTable = (sectionKey, sectionRows) => {
    const section = SECTION_CONFIG[sectionKey];

    return (
      <Paper
        key={sectionKey}
        sx={{
          overflowX: 'auto',
          mb: 3,
          backgroundColor: section.paperBg,
          border: `1px solid ${section.borderColor}`,
          borderRadius: 2
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            px: 2,
            py: 1.5,
            backgroundColor: section.headerBg,
            borderBottom: `2px solid ${section.borderColor}`
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {section.title}{' '}
              <Typography component="span" variant="body2" color="text.secondary">
                ({sectionRows.length})
              </Typography>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {section.subtitle}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownloadCsv(sectionKey, sectionRows)}
            disabled={loading || sectionRows.length === 0}
            sx={{ borderColor: section.borderColor, color: section.borderColor }}
          >
            Download CSV
          </Button>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Mode</TableCell>
              <TableCell>Payment ID</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Access ends</TableCell>
              <TableCell>Current Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9}>Loading…</TableCell>
              </TableRow>
            ) : sectionRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}>No subscribers in this group.</TableCell>
              </TableRow>
            ) : (
              sectionRows.map((sub) => (
                <TableRow key={sub._id}>
                  <TableCell>{sub.customerName}</TableCell>
                  <TableCell>{sub.email}</TableCell>
                  <TableCell>{sub.phone}</TableCell>
                  <TableCell>{sub.subscriptionMode}</TableCell>
                  <TableCell>{sub.paymentDetails?.razorpay_payment_id || '—'}</TableCell>
                  <TableCell>{sub.monthlyAmount}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" component="span">
                        {formatAccessEnd(sub)}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="Edit access end date"
                        onClick={() => openEditAccessEnd(sub)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>{renderStatusCell(sub)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => setDeleteTarget(sub)}
                      aria-label="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  const openEditAccessEnd = (sub) => {
    setEditAccessEndTarget(sub);
    setEditEndingAt(formatAccessEndDateInputValue(sub.cancelledAt));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await fetchWithAuth(`${apiBaseUrl}/api/manual-subscriptions`, {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          paymentAmount: Number(form.paymentAmount)
        })
      });
      setForm(emptyForm());
      setFormOpen(false);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to create subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAccessEnd = async () => {
    if (!editAccessEndTarget || !editEndingAt) return;
    setSavingAccessEnd(true);
    setError('');
    try {
      await fetchWithAuth(
        `${apiBaseUrl}/api/manual-subscriptions/${editAccessEndTarget._id}/access-end`,
        {
          method: 'PATCH',
          body: JSON.stringify({ endingAt: editEndingAt })
        }
      );
      setEditAccessEndTarget(null);
      setEditEndingAt('');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to update access end date');
    } finally {
      setSavingAccessEnd(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setError('');
    try {
      await fetchWithAuth(`${apiBaseUrl}/api/manual-subscriptions/${deleteTarget._id}`, {
        method: 'DELETE'
      });
      setDeleteTarget(null);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to delete subscription');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Manual subscribers (Trial / Gift / India Mags)
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>
          Add subscription
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {renderSubscriptionTable('active', partitioned.active)}
      {renderSubscriptionTable('endingSoon', partitioned.endingSoon)}
      {renderSubscriptionTable('ended', partitioned.ended)}

      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add manual subscription</DialogTitle>
        <form onSubmit={handleCreate}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel id="manual-mode-label">Subscription mode</InputLabel>
              <Select
                labelId="manual-mode-label"
                label="Subscription mode"
                value={form.subscriptionMode}
                onChange={(e) => setForm((f) => ({ ...f, subscriptionMode: e.target.value }))}
              >
                {modes.map((mode) => {
                  const code = typeof mode === 'string' ? mode : mode.code;
                  const label = typeof mode === 'string' ? mode : mode.label || mode.code;
                  return (
                    <MenuItem key={code} value={code}>
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <TextField
              label="Customer name"
              required
              value={form.customerName}
              onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
            />
            <TextField
              label="Email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
            <TextField
              label="Phone (10 digits)"
              required
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
            <TextField
              label="Child age"
              required
              value={form.childAge}
              onChange={(e) => setForm((f) => ({ ...f, childAge: e.target.value }))}
            />
            <FormControl fullWidth required>
              <InputLabel id="child-gender-label">Child gender</InputLabel>
              <Select
                labelId="child-gender-label"
                label="Child gender"
                value={form.childGender}
                onChange={(e) => setForm((f) => ({ ...f, childGender: e.target.value }))}
              >
                <MenuItem value="boy">Boy</MenuItem>
                <MenuItem value="girl">Girl</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Payment ID"
              required
              value={form.paymentId}
              onChange={(e) => setForm((f) => ({ ...f, paymentId: e.target.value }))}
            />
            <TextField
              label="Payment amount (INR)"
              type="number"
              required
              inputProps={{ min: 0, step: '0.01' }}
              value={form.paymentAmount}
              onChange={(e) => setForm((f) => ({ ...f, paymentAmount: e.target.value }))}
            />
            <TextField
              label="Access ending date"
              type="date"
              required
              InputLabelProps={{ shrink: true }}
              value={form.endingAt}
              onChange={(e) => setForm((f) => ({ ...f, endingAt: e.target.value }))}
              helperText="Stored as cancelledAt = one month before this date (member access ends on this date)."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={Boolean(editAccessEndTarget)}
        onClose={() => !savingAccessEnd && setEditAccessEndTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Change access end date</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {editAccessEndTarget?.customerName} — updates{' '}
            <strong>cancelledAt</strong> to one month before this date.
          </Typography>
          <TextField
            label="Access ending date"
            type="date"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            value={editEndingAt}
            onChange={(e) => setEditEndingAt(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditAccessEndTarget(null)} disabled={savingAccessEnd}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveAccessEnd} disabled={savingAccessEnd || !editEndingAt}>
            {savingAccessEnd ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete subscription?</DialogTitle>
        <DialogContent>
          <Typography>
            Remove {deleteTarget?.customerName} ({deleteTarget?.subscriptionMode})? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManualSubscriptionsTab;
